/**
 * Analytics API Layer.
 */

import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type {
  CashFlowReport,
  CreditImpactReport,
  InventorySummary,
  ProfitabilityReport,
  SalesPerformanceReport,
  TaxReport,
} from "@/types/analytics";

export function useInventorySummary() {
  return useQuery<InventorySummary>({
    queryKey: ["analytics", "inventory-summary"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/analytics/inventory-summary"))
        .data as InventorySummary,
  });
}

export function useProfitability(dateFrom?: string, dateTo?: string) {
  return useQuery<ProfitabilityReport>({
    queryKey: ["analytics", "profitability", dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      return (await apiClient.get(`/api/v1/analytics/profitability?${params}`))
        .data as ProfitabilityReport;
    },
  });
}

export function useCreditImpact() {
  return useQuery<CreditImpactReport>({
    queryKey: ["analytics", "credit-impact"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/analytics/credit-impact"))
        .data as CreditImpactReport,
  });
}

export function useCashFlow() {
  return useQuery<CashFlowReport>({
    queryKey: ["analytics", "cash-flow"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/analytics/cash-flow"))
        .data as CashFlowReport,
  });
}

export function useSalesPerformance() {
  return useQuery<SalesPerformanceReport>({
    queryKey: ["analytics", "sales-performance"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/analytics/sales-performance"))
        .data as SalesPerformanceReport,
  });
}

export function useTaxReport(year?: number) {
  return useQuery<TaxReport>({
    queryKey: ["analytics", "tax-report", year],
    queryFn: async () => {
      const params = year ? `?year=${year}` : "";
      return (await apiClient.get(`/api/v1/analytics/tax-report${params}`))
        .data as TaxReport;
    },
  });
}
